"use client"

import { useEffect, useState } from "react"
import { auth, db } from "../Firebase/firebase-config"
import { ref, get, onValue } from "firebase/database"
import { useNavigate } from "react-router-dom"
import PartnerNavbar from "./partner-navbar"
import ProfileDialog from "./profile-dialog"
import OrderStatus from "../Components/OrderStatus"
import UserStockStatus from "../StockStatus/UserStockStatus"
import "./PartnerDashboard.css"
import { Building2, Package, UserCheck, Warehouse } from "lucide-react"

export default function PartnerDashboard() {
  const [partnerName, setPartnerName] = useState("")
  const [partnerProfile, setPartnerProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileOpen, setProfileOpen] = useState(false)
  const [mainUserId, setMainUserId] = useState(null)
  const [activeSection, setActiveSection] = useState(null)
  const [selectedEntity, setSelectedEntity] = useState(null) // "main" or firmId
  const [firms, setFirms] = useState([])
  const [entityName, setEntityName] = useState("")
  const navigate = useNavigate()

  useEffect(() => {
    window.history.pushState(null, "", window.location.href)
    const handlePopState = () => window.history.go(1)
    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [])

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/partner-login", { replace: true })
        return
      }

      try {
        const profileRef = ref(db, "profileDashboard")
        const profileSnap = await get(profileRef)

        if (!profileSnap.exists()) {
          alert("No profile data found!")
          setLoading(false)
          return
        }

        const profiles = profileSnap.val()
        let mainUserIdTemp = null
        let partnerData = null

        for (const userId in profiles) {
          if (profiles[userId].partners && profiles[userId].partners[user.uid]) {
            mainUserIdTemp = userId
            partnerData = profiles[userId].partners[user.uid]
            break
          }
        }

        if (!mainUserIdTemp) {
          alert("You are not assigned to any main user!")
          await auth.signOut()
          navigate("/partner-login", { replace: true })
          return
        }

        setMainUserId(mainUserIdTemp)
        setPartnerName(partnerData?.name || partnerData?.username || "Partner")
        setPartnerProfile(partnerData || null)
        setLoading(false)

        const assignedFirmsIds = partnerData?.assignedFirms || []
        const profileSnapshot = await get(ref(db, `profileDashboard/${mainUserIdTemp}`))
        if (profileSnapshot.exists()) {
          const profileData = profileSnapshot.val()
          const allFirms = profileData.firms || {}

          const filteredFirms = Object.entries(allFirms)
            .filter(([firmId]) => assignedFirmsIds.includes(firmId))
            .map(([firmId, firmData]) => ({
              id: firmId,
              name: firmData.company_name || "Unnamed Firm",
              ...firmData,
            }))

          setFirms(filteredFirms)
        }

        setSelectedEntity("main")
        setEntityName("Main User")

        const partnerRef = ref(db, `profileDashboard/${mainUserIdTemp}/partners/${user.uid}`)
        const profileListener = onValue(partnerRef, (snap) => {
          if (!snap.exists()) {
            alert("You are no longer a partner. Logging out...")
            auth.signOut().then(() => {
              navigate("/partner-login", { replace: true })
            })
          }
        })

        return () => {
          profileListener()
        }
      } catch (error) {
        console.error("Error loading partner profile:", error)
        setLoading(false)
      }
    })

    return () => unsubscribe()
  }, [navigate])

  const handleLogout = async () => {
    localStorage.removeItem("userType")
    localStorage.removeItem("mainUserId")
    localStorage.removeItem("selectedAccountUid")
    await auth.signOut()
    navigate("/partner-login", { replace: true })
  }

  const handleSelectEntity = (entityType, entityId = null) => {
    if (entityType === "main") {
      setSelectedEntity("main")
      setEntityName("Main User")
    } else {
      const firm = firms.find((f) => f.id === entityId)
      setSelectedEntity(entityId)
      setEntityName(firm?.name || "Firm")
    }
    setActiveSection(null)
  }

 if (loading) {
    return (
      <div className="partner-loading">
        <div className="spinner"></div>
       
      </div>
    )
  }

  return (
    <>
      <PartnerNavbar
        title="Partner Portal"
        userName={partnerName}
        onProfile={() => setProfileOpen(true)}
        onLogout={handleLogout}
      />

      <main className="partner-dashboard">
        <div className="partner-dashboard-container">
          {/* Header */}
          <div className="partner-dashboard-header">
            <h1>Welcome back, <span className="highlight">{partnerName}</span></h1>
            <p className="subtitle">Select an entity to manage orders and stock</p>
          </div>

          {/* Entity Selector */}
          <section className="entity-section">
            <h2 className="partner-section-title">Choose Entity</h2>
            <div className="entity-grid">
              {/* Main Account */}
              <div
                className={`entity-card ${selectedEntity === "main" ? "selected" : ""}`}
                onClick={() => handleSelectEntity("main")}
              >
                <div className="card-glow"></div>
                <UserCheck size={48} className="entity-icon main" />
                <h3>Main Account</h3>
                <p>View all orders & stock</p>
                {selectedEntity === "main" && <div className="selected-badge">Active</div>}
              </div>

              {/* Firms */}
              {firms.map((firm) => (
                <div
                  key={firm.id}
                  className={`entity-card ${selectedEntity === firm.id ? "selected" : ""}`}
                  onClick={() => handleSelectEntity("firm", firm.id)}
                >
                  <div className="card-glow"></div>
                  <Building2 size={48} className="entity-icon firm" />
                  <h3>{firm.name}</h3>
                  <p className="firm-id">View all orders & stock</p>
                  {selectedEntity === firm.id && <div className="selected-badge">Active</div>}
                </div>
              ))}
            </div>
          </section>

          {/* Actions */}
          {selectedEntity && (
            <section className="actions-section">
              <div className="entity-header">
                {/* <h2>Managing: <span className="entity-name">{entityName}</span></h2> */}
              </div>

              <div className="action-grid">
                <div
                  className={`action-card ${activeSection === "orders" ? "active" : ""}`}
                  onClick={() => setActiveSection(activeSection === "orders" ? null : "orders")}
                >
                  <Package size={40} />
                  <h3>Order Status</h3>
                  <p>Track and update order progress</p>
                </div>

                <div
                  className={`action-card ${activeSection === "stock" ? "active" : ""}`}
                  onClick={() => setActiveSection(activeSection === "stock" ? null : "stock")}
                >
                  <Warehouse size={40} />
                  <h3>Stock Status</h3>
                  <p>Update current inventory</p>
                </div>
              </div>

              {/* Content */}
              <div className="full-width-content">
                {activeSection === "orders" && (
                  <div className="partner-content-wrapper">
                    <OrderStatus
                      targetFirmAndMainIds={
                        selectedEntity === "main"
                          ? [mainUserId, ...firms.map(f => f.id)]
                          : [selectedEntity]
                      }
                      isPartner={true}
                    />
                  </div>
                )}

                {activeSection === "stock" && (
                  <div className="partner-content-wrapper">
                    <UserStockStatus
                      targetUserId={selectedEntity === "main" ? mainUserId : selectedEntity}
                    />
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </main>

      <ProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        profile={partnerProfile}
        title="Partner Profile"
      />
    </>
  )
}